import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * @fileoverview QuickActions component for Event Cards
 *
 * Displays primary CTA and secondary action buttons with responsive
 * behavior and overflow management.
 *
 * @author LOCI Development Team
 * @since 1.0.0
 */
import { useState, useRef, useEffect } from 'react';
import { ActionType } from '@loci/types';
import { useEventActions } from '../../hooks/useEventActions';
import { Button } from '../Button/Button';
/**
 * QuickActions component for displaying event card actions
 *
 * Features:
 * - Responsive primary CTA based on event state
 * - Secondary actions with overflow menu on mobile
 * - Calendar integration with provider selection
 * - Loading and error states
 * - Accessibility support
 *
 * @example
 * ```tsx
 * <QuickActions
 *   event={eventData}
 *   user={currentUser}
 *   onActionClick={(action) => console.log('Action clicked:', action)}
 * />
 * ```
 */
export function QuickActions({ event, user, className = '', onActionClick, onCalendarClick, }) {
    const { primaryCTA, secondaryActions, handlePrimaryClick, handleSecondaryClick, handleCalendarClick, isLoading, error, } = useEventActions({
        event,
        user,
        onActionClick,
        onCalendarClick,
    });
    const [showOverflow, setShowOverflow] = useState(false);
    const [showCalendarMenu, setShowCalendarMenu] = useState(false);
    const calendarMenuRef = useRef(null);
    const overflowMenuRef = useRef(null);
    // Close calendar menu when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (calendarMenuRef.current && !calendarMenuRef.current.contains(event.target)) {
                setShowCalendarMenu(false);
            }
        };
        if (showCalendarMenu) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => document.removeEventListener('mousedown', handleClickOutside);
        }
    }, [showCalendarMenu]);
    // Close overflow menu when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (overflowMenuRef.current && !overflowMenuRef.current.contains(event.target)) {
                setShowOverflow(false);
            }
        };
        if (showOverflow) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => document.removeEventListener('mousedown', handleClickOutside);
        }
    }, [showOverflow]);
    // Split actions into inline and overflow
    const inlineActions = secondaryActions.filter((a) => a.placement === 'inline');
    const overflowActions = secondaryActions.filter((a) => a.placement === 'overflow');
    // Handle secondary action click with calendar logic
    const handleSecondaryActionClick = async (action) => {
        if (action === ActionType.ADD_TO_CALENDAR) {
            setShowCalendarMenu(!showCalendarMenu);
            setShowOverflow(false);
        }
        else {
            await handleSecondaryClick(action);
            setShowOverflow(false);
            setShowCalendarMenu(false);
        }
    };
    // Calendar provider options
    const calendarProviders = [
        { value: 'google', label: 'Google Calendar', icon: '📅' },
        { value: 'outlook', label: 'Outlook', icon: '📧' },
        { value: 'apple', label: 'Apple Calendar', icon: '🍎' },
        { value: 'ics', label: 'Download ICS', icon: '💾' },
    ];
    return (_jsxs("div", { className: `quick-actions ${className}`, children: [error && (_jsx("div", { className: "quick-actions__error", role: "alert", children: error })), _jsxs("div", { className: "quick-actions__container", children: [_jsxs(Button, { variant: "primary", size: "md", onClick: handlePrimaryClick, disabled: primaryCTA.disabled || isLoading, loading: isLoading, className: "quick-actions__primary", "aria-label": primaryCTA.label, title: primaryCTA.disabledReason, children: [primaryCTA.isExternal && _jsx("span", { className: "external-icon", children: "\u2197" }), primaryCTA.label] }), _jsxs("div", { className: "quick-actions__secondary", children: [inlineActions.map((action) => (_jsxs("button", { onClick: () => handleSecondaryActionClick(action.type), className: "quick-actions__icon-btn", "aria-label": action.label, title: action.tooltip, children: [_jsx("span", { className: "icon", "aria-hidden": "true", children: getIconForAction(action.icon) }), _jsx("span", { className: "sr-only", children: action.label })] }, action.type))), showCalendarMenu && (_jsx("div", { className: "quick-actions__calendar-menu", ref: calendarMenuRef, children: calendarProviders.map((provider) => (_jsxs("button", { onClick: () => {
                                        handleCalendarClick(provider.value);
                                        setShowCalendarMenu(false);
                                    }, className: "quick-actions__menu-item", children: [_jsx("span", { className: "menu-icon", children: provider.icon }), provider.label] }, provider.value))) })), overflowActions.length > 0 && (_jsxs("div", { className: "quick-actions__overflow", ref: overflowMenuRef, children: [_jsx("button", { onClick: () => {
                                            setShowOverflow(!showOverflow);
                                            setShowCalendarMenu(false);
                                        }, className: "quick-actions__icon-btn", "aria-label": "More actions", "aria-expanded": showOverflow, "aria-haspopup": "true", children: _jsx("span", { className: "icon", "aria-hidden": "true", children: "\u2022\u2022\u2022" }) }), showOverflow && (_jsx("div", { className: "quick-actions__overflow-menu", children: overflowActions.map((action) => (_jsxs("button", { onClick: () => handleSecondaryActionClick(action.type), className: "quick-actions__menu-item", children: [_jsx("span", { className: "menu-icon", children: getIconForAction(action.icon) }), action.label] }, action.type))) }))] }))] })] }), _jsx("style", { dangerouslySetInnerHTML: {
                    __html: `
            .quick-actions { width: 100%; }
            .quick-actions__error {
              background: #fee; color: #c00; padding: 8px 12px; border-radius: 4px;
              margin-bottom: 12px; font-size: 14px;
            }
            .quick-actions__container {
              display: flex; gap: 8px; align-items: center;
            }
            .quick-actions__primary { flex: 1; min-height: 44px; }
            .quick-actions__secondary {
              display: flex; gap: 8px; align-items: center; position: relative;
            }
            .quick-actions__icon-btn {
              min-width: 44px; min-height: 44px; padding: 10px;
              background: transparent; border: 1px solid #e1e5e9; border-radius: 8px;
              cursor: pointer; transition: all 0.2s ease;
              display: flex; align-items: center; justify-content: center;
            }
            .quick-actions__icon-btn:hover {
              background: #f8f9fa; border-color: #667eea;
            }
            .quick-actions__icon-btn:active { transform: scale(0.95); }
            .quick-actions__overflow { position: relative; }
            .quick-actions__overflow-menu,
            .quick-actions__calendar-menu {
              position: absolute; right: 0; bottom: calc(100% + 8px);
              background: white; border: 1px solid #e1e5e9; border-radius: 8px;
              box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
              min-width: 200px; z-index: 1000; overflow: hidden;
            }
            .quick-actions__menu-item {
              width: 100%; padding: 12px 16px; background: none; border: none;
              text-align: left; cursor: pointer; display: flex; align-items: center;
              gap: 12px; transition: background 0.2s ease; font-size: 14px; color: #333;
            }
            .quick-actions__menu-item:hover { background: #f8f9fa; }
            .quick-actions .menu-icon { font-size: 18px; }
            .quick-actions .external-icon { margin-right: 4px; font-size: 12px; }
            .quick-actions .sr-only {
              position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px;
              overflow: hidden; clip: rect(0, 0, 0, 0); white-space: nowrap; border-width: 0;
            }
            @media (max-width: 768px) {
              .quick-actions__primary { width: 100%; }
              .quick-actions__overflow-menu,
              .quick-actions__calendar-menu {
                bottom: auto; top: calc(100% + 8px);
              }
            }
          `,
                } })] }));
}
/**
 * Get simple icon representation for action type
 * In production, this should use a proper icon library like Lucide
 */
function getIconForAction(iconName) {
    const iconMap = {
        CalendarPlus: '📅',
        Bookmark: '🔖',
        BookmarkCheck: '✓',
        Share2: '↗️',
        Info: 'ℹ️',
        Video: '▶️',
        Ticket: '🎫',
        Clock: '⏰',
        Settings: '⚙️',
        PlayCircle: '▶️',
        ExternalLink: '↗',
    };
    return iconMap[iconName] || '•';
}
